import React, { useState, useEffect, useRef } from 'react';
import { productsAPI, categoriesAPI } from '../../utils/api';
import { LoadingSpinner, StatusBadge } from '../../components/common';

const EMPTY_FORM = { name: '', description: '', category_id: '', price: '', compare_price: '', stock_qty: 0, low_stock_threshold: 5, image_url: '', image_data: '', brand: '', weight: '', colors: '', is_active: 1 };

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [nameWarning, setNameWarning] = useState('');
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    categoriesAPI.getAll().then(r => setCategories(r.data));
  }, []);

  const load = () => {
    if (!search && !catFilter) {
      setProducts([]);
      setTotalPages(1);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    productsAPI.getAll({ search, category: catFilter, limit: 15, page })
      .then(pRes => {
        setProducts(pRes.data.products);
        setTotalPages(pRes.data.pages || 1);
        setTotalCount(pRes.data.total || 0);
      }).finally(() => setLoading(false));
  };

  useEffect(() => { setPage(1); }, [search, catFilter]);
  useEffect(() => { load(); }, [search, catFilter, page]); // eslint-disable-line

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const imageInputRef = useRef();

  const handleImageFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(p => ({ ...p, image_data: ev.target.result, image_url: '' }));
      if (imageInputRef.current) imageInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => setForm(p => ({ ...p, image_data: '', image_url: '' }));

  const openNew = () => { setForm(EMPTY_FORM); setEditing(null); setShowForm(true); setError(''); setNameWarning(''); };
  const openEdit = (p) => { setForm({ ...p, category_id: p.category_id || '' }); setEditing(p.id); setShowForm(true); setError(''); setNameWarning(''); };

  const handleNameChange = (val) => {
    set('name', val);
    const trimmed = val.trim().toLowerCase();
    if (trimmed) {
      const dup = products.find(p => p.name.toLowerCase() === trimmed && p.id !== editing);
      setNameWarning(dup ? `A product named "${dup.name}" already exists (SKU: ${dup.sku || 'n/a'}).` : '');
    } else {
      setNameWarning('');
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.price) { setError('Name and Price are required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) await productsAPI.update(editing, form);
      else await productsAPI.create(form);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this product?')) return;
    await productsAPI.delete(id);
    load();
  };

  const handleStockUpdate = async (id, current) => {
    const val = window.prompt('Enter new stock quantity:', current);
    if (val === null) return;
    const qty = parseInt(val);
    if (isNaN(qty) || qty < 0) { alert('Invalid quantity'); return; }
    await productsAPI.updateStock(id, qty);
    load();
  };

  const toggleSort = (col) => { setSortDir(sortCol === col && sortDir === 'asc' ? 'desc' : 'asc'); setSortCol(col); };
  const si = (col) => <span style={{ fontSize: '0.65rem', marginLeft: 2, opacity: sortCol === col ? 1 : 0.3 }}>{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>;
  const sortedProducts = [...products].sort((a, b) => {
    const va = a[sortCol] ?? ''; const vb = b[sortCol] ?? '';
    const n = parseFloat(va), m = parseFloat(vb);
    const cmp = !isNaN(n) && !isNaN(m) ? n - m : String(va).toLowerCase().localeCompare(String(vb).toLowerCase());
    return sortDir === 'desc' ? -cmp : cmp;
  });

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><h1>Products</h1><p>{!search && !catFilter ? '0' : totalCount} products</p></div>
        <button className="btn btn-primary" onClick={openNew}>+ Add Product</button>
      </div>

      {/* Filters */}
      <div className="flex gap-3" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input className="form-input" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 240 }} />
        <select className="form-select" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #2563eb' }}>
          <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>{editing ? 'Edit Product' : 'New Product'}</h2>
          {error && <div className="alert alert-error">{error}</div>}
          {editing && form.sku && (
            <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
              SKU: <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#111' }}>{form.sku}</span>
            </div>
          )}
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label className="form-label">Product Name *</label>
            <input className="form-input" value={form.name} onChange={e => handleNameChange(e.target.value)} style={nameWarning ? { borderColor: '#f59e0b' } : {}} />
            {nameWarning && <div style={{ marginTop: '0.3rem', fontSize: '0.78rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 5, padding: '0.3rem 0.5rem' }}>⚠ {nameWarning}</div>}
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select w-full" value={form.category_id} onChange={e => set('category_id', e.target.value)}>
                <option value="">-- Select --</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Price ($) *</label>
              <input className="form-input" type="number" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Compare Price ($)</label>
              <input className="form-input" type="number" step="0.01" value={form.compare_price} onChange={e => set('compare_price', e.target.value)} />
            </div>
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Stock Qty</label>
              <input className="form-input" type="number" value={form.stock_qty} onChange={e => set('stock_qty', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Low Stock Alert</label>
              <input className="form-input" type="number" value={form.low_stock_threshold} onChange={e => set('low_stock_threshold', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Brand</label>
              <input className="form-input" value={form.brand} onChange={e => set('brand', e.target.value)} />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Product Image</label>
              {/* Preview */}
              {(form.image_data || form.image_url) && (
                <div style={{ marginBottom: '0.5rem', position: 'relative', display: 'inline-block' }}>
                  <img
                    src={form.image_data || form.image_url}
                    alt="preview"
                    style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1.5px solid #e5e7eb' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <button
                    onClick={clearImage}
                    title="Remove image"
                    style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: '0.7rem', lineHeight: '20px', textAlign: 'center', padding: 0 }}
                  >✕</button>
                </div>
              )}
              {/* Upload button */}
              <label
                htmlFor="product-img-input"
                style={{ display: 'inline-block', background: '#6366f1', color: '#fff', padding: '0.35rem 0.85rem', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', marginBottom: '0.4rem', userSelect: 'none' }}
              >
                📁 Upload Image
              </label>
              <input
                id="product-img-input"
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ position: 'absolute', opacity: 0, width: 1, height: 1, overflow: 'hidden' }}
                onChange={handleImageFile}
              />
              {/* OR URL */}
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '0.3rem' }}>— or paste a URL —</div>
              <input
                className="form-input"
                value={form.image_data ? '' : form.image_url}
                onChange={e => setForm(p => ({ ...p, image_url: e.target.value, image_data: '' }))}
                placeholder="https://..."
                disabled={!!form.image_data}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Weight (g)</label>
              <input className="form-input" type="number" value={form.weight} onChange={e => set('weight', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Available Colors <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>(comma-separated, e.g. Red, Blue, Green)</span></label>
            <input className="form-input" value={form.colors || ''} onChange={e => set('colors', e.target.value)} placeholder="Red, Blue, Green, Yellow" />
            {form.colors && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem' }}>
                {form.colors.split(',').map(c => c.trim()).filter(Boolean).map(c => (
                  <span key={c} style={{ background: '#dbeafe', color: '#1e40af', borderRadius: 999, padding: '0.15rem 0.65rem', fontSize: '0.78rem', fontWeight: 600 }}>{c}</span>
                ))}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          {editing && (
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.is_active} onChange={e => set('is_active', e.target.value)}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          )}
          <div className="flex gap-2" style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Product'}</button>
            <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>Product {si('name')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('sku')}>SKU {si('sku')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('category_name')}>Category {si('category_name')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('price')}>Price {si('price')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('stock_qty')}>Stock {si('stock_qty')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('is_active')}>Status {si('is_active')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProducts.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        {(p.image_data || p.image_url) ? (
                          <img src={p.image_data || p.image_url} alt={p.name}
                            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', flexShrink: 0 }}
                            onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div style={{ width: 36, height: 36, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>📦</div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{p.name}</div>
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{p.brand}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{p.sku}</td>
                    <td style={{ fontSize: '0.82rem' }}>{p.category_name || '—'}</td>
                    <td style={{ fontWeight: 700 }}>${parseFloat(p.price).toFixed(2)}</td>
                    <td>
                      <button
                        onClick={() => handleStockUpdate(p.id, p.stock_qty)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
                        className={p.stock_qty <= p.low_stock_threshold ? 'text-danger' : 'text-success'}
                        title="Click to update stock"
                      >
                        {p.stock_qty} ✎
                      </button>
                    </td>
                    <td>
                      <span className={`badge ${p.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#6b7280', padding: '3rem', fontSize: '1rem' }}>
                    {!search && !catFilter ? 'Please select a category or search to view products.' : 'No products found'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e5e7eb' }}>
              <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>Page {page} of {totalPages}</span>
              <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
